import type { CardId } from '@open-songster/shared'
import { CARDS_PER_YEAR, DECK_START_YEAR, SONGSTER_URI_SCHEME } from '@open-songster/shared'

/** Stable string form of a card ID, used as the assignment map key. */
export function cardIdToKey(cardId: CardId): string {
  return `${cardId.year}/${cardId.index}`
}

export function cardIdFromKey(key: string, maxYear: number = currentDeckYear()): CardId | null {
  return parseCardIdPath(key, maxYear)
}

export function formatSongsterUri(cardId: CardId): string {
  return `${SONGSTER_URI_SCHEME}://${cardIdToKey(cardId)}`
}

/**
 * Parses a scanned QR payload. Returns null for anything that is not a
 * well-formed `songster://<year>/<index>` URI — the caller shows a friendly
 * message and stays in the game.
 */
export function parseSongsterUri(raw: string, maxYear: number = currentDeckYear()): CardId | null {
  const trimmed = raw.trim()
  const prefix = `${SONGSTER_URI_SCHEME}://`
  if (!trimmed.toLowerCase().startsWith(prefix)) return null
  return parseCardIdPath(trimmed.slice(prefix.length), maxYear)
}

function parseCardIdPath(path: string, maxYear: number): CardId | null {
  const match = /^(\d{4})\/(\d{1,2})$/.exec(path.trim())
  if (!match) return null
  const [, yearPart, indexPart] = match
  if (yearPart === undefined || indexPart === undefined) return null
  const year = Number(yearPart)
  const index = Number(indexPart)
  if (year < DECK_START_YEAR || year > maxYear) return null
  if (index < 1 || index > CARDS_PER_YEAR) return null
  return { year, index }
}

export function currentDeckYear(now: Date = new Date()): number {
  return now.getFullYear()
}

/** All years of a full deck, ascending. */
export function deckYears(maxYear: number = currentDeckYear()): number[] {
  const years: number[] = []
  for (let year = DECK_START_YEAR; year <= maxYear; year++) years.push(year)
  return years
}

/** The five card IDs of one year, in index order. */
export function cardIdsForYear(year: number): CardId[] {
  return Array.from({ length: CARDS_PER_YEAR }, (_, i) => ({ year, index: i + 1 }))
}
